import { Injectable } from '@angular/core';
import { AuthService } from 'src/app/services/auth.service';
import {
	SystemCapability,
	SystemCapabilityEnum,
	SystemCapabilityName,
	systemCapabilityToEnum,
	nameToEnum,
} from 'src/app/models/data/systemCapability';
import { UserProfileService } from '../user-profile.service';
import { Subject } from 'rxjs';

@Injectable({
	providedIn: 'root',
})
export class SystemCapabilityService {
	private userSystemCapabilityEnums: SystemCapabilityEnum[] = [];
	private userSystemCapabilities: SystemCapability[] = [];

	systemCapabilitiesUpdated = new Subject<SystemCapability[]>();

	public isOracle = false;
	public isCompanyAdmin = false;
	public isSupport = false;

	constructor(private readonly authService: AuthService) {
		authService.userSession().subscribe((it) => {
			this.userSystemCapabilities = it.systemCapabilities ?? [];
			this.userSystemCapabilityEnums = toEnums(it.systemCapabilities ?? []);

			this.isOracle = !!it.assignedRole?.role?.isOracle;
			this.isCompanyAdmin = !!it.assignedRole?.role?.isCompanyAdmin;
			this.isSupport = !!it.assignedRole?.role?.isSupport;

			this.systemCapabilitiesUpdated.next(this.userSystemCapabilities);

			// Providing Console output of existing capabilities to make UAT easier.
			// This can be removed after SC testing is complete.
			// TODO: REMOVE AFTER UAT
			// eslint-disable-next-line no-console
			console.log(
				"\nUser's session was updated:\n" +
					'\n+-----------------------------------------+' +
					`\n|   active ER Id    : ${(it.claims?.activeEntityRelationshipId ?? '').toString().padEnd(20, ' ')}|` +
					`\n|   user_id         : ${(it.claims?.user_id ?? '').toString().padEnd(20, ' ')}|` +
					`\n|   sessionInactive : ${(it.claims?.sessionInactive ?? '').toString().padEnd(20, ' ')}|` +
					`\n|   isOracle        : ${(this.isOracle ? 'true' : 'false').toString().padEnd(20, ' ')}|` +
					`\n|   isCompanyAdmin  : ${(this.isCompanyAdmin ? 'true' : 'false').toString().padEnd(20, ' ')}|` +
					`\n|   isSupport       : ${(this.isSupport ? 'true' : 'false').toString().padEnd(20, ' ')}|` +
					'\n+-----------------------------------------+' +
					'\n|   Assigned System Capabilities:         |' +
					this.userSystemCapabilities
						.map((item) => `\n|      ${item.name.padEnd(35, ' ')}|`)
						.join('') +
					'\n+-----------------------------------------+\n '
			);
		});
	}

	private getEnum(
		systemCapability: SystemCapabilityEnum | SystemCapabilityName
	): SystemCapabilityEnum | undefined {
		if (typeof systemCapability === 'string') {
			return nameToEnum(systemCapability as string) as SystemCapabilityEnum;
		}
		return systemCapability as SystemCapabilityEnum;
	}

	private getEnums(
		systemCapabilities: (SystemCapabilityEnum | SystemCapabilityName)[]
	): SystemCapabilityEnum[] {
		const caps = systemCapabilities.map((item) => this.getEnum(item));
		return caps.filter((item) => !!item) as SystemCapabilityEnum[];
	}

	public has(
		systemCapability: SystemCapabilityEnum | SystemCapabilityName
	): boolean {
		const scEnum = this.getEnum(systemCapability);
		// tslint:disable-next-line no-non-null-assertion
		return this.userSystemCapabilityEnums.includes(scEnum!);
	}

	public hasOne(
		systemCapabilities: (SystemCapabilityEnum | SystemCapabilityName)[]
	): boolean {
		const scEnums = this.getEnums(systemCapabilities);
		for (const it of scEnums) {
			if (this.userSystemCapabilityEnums.includes(it)) {
				return true;
			}
		}
		return false;
	}

	public hasAll(
		systemCapabilities: (SystemCapabilityEnum | SystemCapabilityName)[]
	): boolean {
		const scEnums = this.getEnums(systemCapabilities);
		return scEnums.every((it) => this.userSystemCapabilityEnums.includes(it));
	}

	/** GCM act only — never treat View as write authorization. */
	public hasGcmAct(): boolean {
		return this.has(SystemCapabilityEnum.globalCompanyManagement);
	}

	/** GCM act or GCM View — for cross-company read paths. */
	public hasGcmView(): boolean {
		return this.hasOne([
			SystemCapabilityEnum.globalCompanyManagement,
			SystemCapabilityEnum.globalCompanyManagementView,
		]);
	}

	/** Billing (Internal) — view-only company Users path (ITE-1887). */
	public hasBillingManagement(): boolean {
		return this.has(SystemCapabilityEnum.billingManagement);
	}

	/** Device Management act or View — for tab/list visibility. */
	public hasDeviceManagementView(): boolean {
		return this.hasOne([
			SystemCapabilityEnum.deviceManagement,
			SystemCapabilityEnum.deviceManagementView,
		]);
	}

	/** Generate Codes act or View — for tab visibility. */
	public hasIssueFlexCodesView(): boolean {
		return this.hasOne([
			SystemCapabilityEnum.issueFlexCodes,
			SystemCapabilityEnum.issueFlexCodesView,
		]);
	}

	public getAllCapabilities(): SystemCapability[] {
		return this.userSystemCapabilities;
	}

	public getAllCapabilityEnums(): SystemCapabilityEnum[] {
		return this.userSystemCapabilityEnums;
	}
}

const toEnums = (
	systemCapabilities: SystemCapability[]
): SystemCapabilityEnum[] =>
	systemCapabilities
		.map(systemCapabilityToEnum)
		.filter((it) => !!it) as SystemCapabilityEnum[];
